export const CART_CHANGE_EVENT = 'runaway:cart-change';

function formatWithDelimiters(number, precision, thousands, decimal) {
  const fixed = (Number(number) / 100).toFixed(precision);
  const [whole, fraction] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
}

export function selectVariant(product, optionValues) {
  if (!Array.isArray(product?.variants) || !Array.isArray(optionValues) || optionValues.length === 0) {
    return null;
  }

  return product.variants.find((variant) => {
    return Array.isArray(variant.options) &&
      variant.options.length === optionValues.length &&
      variant.options.every((value, index) => value === optionValues[index]);
  }) ?? null;
}

export function formatMoney(cents, format = '${{ amount }}') {
  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
    amount_with_apostrophe_separator: [2, "'", '.'],
  };

  const textFormat = String(format).replace(/<[^>]*>/g, '') || '${{ amount }}';
  return textFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_placeholder, key) => {
    const settings = formats[key] ?? formats.amount;
    return formatWithDelimiters(cents, ...settings);
  });
}

export function shopifyRoutesRoot(context = globalThis) {
  let rawRoot;
  if (typeof context === 'string') {
    rawRoot = context;
  } else {
    const viewport = context?.nodeType === 9
      ? context.defaultView
      : context?.ownerDocument?.defaultView || context?.defaultView || context;
    rawRoot = viewport?.Shopify?.routes?.root || context?.Shopify?.routes?.root;
  }

  if (typeof rawRoot !== 'string') return '/';
  const path = rawRoot.trim().split(/[?#]/, 1)[0];
  if (!path.startsWith('/') || path.startsWith('//')) return '/';
  const segments = path.split('/').filter(Boolean);
  return segments.length > 0 ? `/${segments.join('/')}/` : '/';
}

function cartRoute(suffix = '', context = globalThis) {
  return `${shopifyRoutesRoot(context)}cart${suffix}`;
}

async function cartResponse(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // A non-JSON response still receives a useful, stable error below.
  }

  if (!response.ok) {
    throw new Error(
      payload?.description ||
      payload?.message ||
      'The cart could not be updated. Visit the full cart to try again.',
    );
  }

  return payload;
}

export async function addItem(formData, fetcher = fetch, routeContext = globalThis) {
  const response = await fetcher(cartRoute('/add.js', routeContext), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: formData,
  });
  return cartResponse(response);
}

export async function changeItem(line, quantity, fetcher = fetch, routeContext = globalThis) {
  const response = await fetcher(cartRoute('/change.js', routeContext), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
  });
  return cartResponse(response);
}

async function getCart(fetcher = fetch, routeContext = globalThis) {
  const response = await fetcher(cartRoute('.js', routeContext), { headers: { Accept: 'application/json' } });
  return cartResponse(response);
}

function cartDocument(root) {
  return root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
}

function element(documentRoot, tagName, attributes = {}, text = '') {
  const node = documentRoot.createElement(tagName);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== null && value !== undefined) node.setAttribute(name, String(value));
  });
  if (text) node.textContent = text;
  return node;
}

function cartItemElement(documentRoot, item, line, moneyFormat) {
  const cartUrl = cartRoute('', documentRoot);
  const article = element(documentRoot, 'article', {
    class: 'runaway-cart-item',
    'data-cart-item': '',
    'data-cart-key': item.key || '',
  });
  const media = element(documentRoot, 'a', {
    class: 'runaway-cart-item__media',
    href: item.url || cartUrl,
    tabindex: '-1',
    'aria-hidden': 'true',
  });
  if (item.image) {
    media.append(element(documentRoot, 'img', {
      src: item.image,
      alt: item.featured_image?.alt || item.product_title || item.title || '',
      width: '160',
      height: '200',
      loading: 'lazy',
      'data-cart-item-image': '',
    }));
  } else {
    media.append(element(documentRoot, 'span', { class: 'runaway-cart-item__image-empty' }, 'RUNAWAY'));
  }

  const details = element(documentRoot, 'div', { class: 'runaway-cart-item__details' });
  const title = element(documentRoot, 'a', {
    class: 'runaway-cart-item__title',
    href: item.url || cartUrl,
    'data-cart-item-title': '',
  }, item.product_title || item.title || 'Product');
  details.append(title);
  if (item.variant_title && item.variant_title !== 'Default Title') {
    details.append(element(documentRoot, 'p', {
      class: 'runaway-cart-item__variant',
      'data-cart-item-variant': '',
    }, item.variant_title));
  }

  const controls = element(documentRoot, 'div', { class: 'runaway-cart-item__controls' });
  const quantityId = `RunawayCartQuantity-${line}`;
  controls.append(element(documentRoot, 'label', { for: quantityId }, 'Quantity'));
  controls.append(element(documentRoot, 'input', {
    id: quantityId,
    class: 'runaway-cart-item__quantity',
    type: 'number',
    name: 'updates[]',
    min: '0',
    value: Number(item.quantity) || 0,
    inputmode: 'numeric',
    'data-cart-quantity': '',
    'data-line': line,
    'data-current-quantity': Number(item.quantity) || 0,
  }));
  controls.append(element(documentRoot, 'a', {
    class: 'runaway-cart-item__remove',
    href: `${cartRoute('/change', documentRoot)}?line=${line}&quantity=0`,
    'data-cart-remove': '',
    'data-line': line,
  }, 'Remove'));
  details.append(controls);
  details.append(element(documentRoot, 'p', {
    class: 'runaway-cart-item__price',
    'data-cart-item-price': '',
  }, formatMoney(item.final_line_price || 0, moneyFormat)));

  article.append(media, details);
  return article;
}

function updateCartCount(event, root = document) {
  const itemCount = Number(event.detail?.item_count);
  if (!Number.isFinite(itemCount)) return;

  root.querySelectorAll('[data-cart-count]').forEach((node) => {
    node.textContent = String(itemCount);
  });

  root.querySelectorAll('[data-cart-trigger]').forEach((node) => {
    node.setAttribute('aria-label', `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
  });
}

export function renderCartState(root, cart) {
  const documentRoot = cartDocument(root);
  const content = root?.matches?.('[data-cart-drawer-content]')
    ? root
    : root?.querySelector?.('[data-cart-drawer-content]');
  if (!documentRoot || !content) return;

  const items = Array.isArray(cart?.items) ? cart.items : [];
  content.replaceChildren();

  if (items.length === 0) {
    content.append(element(documentRoot, 'p', {
      class: 'runaway-cart-drawer__empty',
      'data-cart-empty': '',
    }, 'Your cart is empty.'));
  } else {
    const form = element(documentRoot, 'form', {
      class: 'runaway-cart-drawer__form',
      action: cartRoute('', documentRoot),
      method: 'post',
      'data-cart-form': '',
    });
    const list = element(documentRoot, 'div', {
      class: 'runaway-cart-drawer__items',
      'data-cart-items': '',
    });
    const moneyFormat = root.dataset?.moneyFormat || '${{ amount }}';
    items.forEach((item, index) => list.append(cartItemElement(documentRoot, item, index + 1, moneyFormat)));

    const summary = element(documentRoot, 'div', {
      class: 'runaway-cart-drawer__summary',
      'data-cart-summary': '',
    });
    const subtotal = element(documentRoot, 'p', { class: 'runaway-cart-drawer__subtotal' });
    subtotal.append(
      element(documentRoot, 'span', {}, 'Subtotal'),
      element(documentRoot, 'span', { 'data-cart-subtotal': '' }, formatMoney(cart?.total_price || 0, moneyFormat)),
    );
    const actions = element(documentRoot, 'div', { class: 'runaway-cart-drawer__actions' });
    actions.append(
      element(documentRoot, 'button', {
        class: 'runaway-button',
        type: 'submit',
        name: 'update',
      }, 'Update cart'),
      element(documentRoot, 'button', {
        class: 'runaway-button runaway-button--purchase',
        type: 'submit',
        name: 'checkout',
        'data-cart-checkout': '',
      }, 'Checkout'),
    );
    summary.append(subtotal, actions);
    form.append(list, summary);
    content.append(form);
  }

  const liveRegion = root.querySelector?.('[data-cart-live]');
  if (liveRegion) liveRegion.textContent = '';
  updateCartCount({ detail: cart }, documentRoot);
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(root) {
  return [...root.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) => {
    return !element.hidden && element.getAttribute('aria-hidden') !== 'true';
  });
}

function trapFocus(event, root) {
  if (event.key !== 'Tab') return;
  const focusable = focusableElements(root);
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }

  const first = focusable[0];
  const last = focusable.at(-1);
  const documentRoot = cartDocument(root);
  if (event.shiftKey && documentRoot?.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && documentRoot?.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function dispatchCartChange(cart, target = document) {
  const documentRoot = target?.nodeType === 9 ? target : cartDocument(target);
  const CustomEventConstructor = documentRoot?.defaultView?.CustomEvent || CustomEvent;
  target.dispatchEvent(new CustomEventConstructor(CART_CHANGE_EVENT, { detail: cart }));
}

export function openCartDrawer(drawer, trigger = cartDocument(drawer)?.activeElement) {
  if (!drawer) return;
  const documentRoot = cartDocument(drawer);
  drawer.__runawayReturnFocus = trigger;
  drawer.hidden = false;
  if (documentRoot) documentRoot.documentElement.dataset.cartOpen = 'true';
  focusableElements(drawer)[0]?.focus();
}

export function closeCartDrawer(drawer) {
  if (!drawer) return;
  const documentRoot = cartDocument(drawer);
  drawer.hidden = true;
  if (documentRoot) delete documentRoot.documentElement.dataset.cartOpen;
  drawer.__runawayReturnFocus?.focus?.();
  drawer.__runawayReturnFocus = null;
}

const menuCleanups = new WeakMap();
const cartDrawerCleanups = new WeakMap();
const productCleanups = new WeakMap();
const documentCleanups = new WeakMap();
const activeDrawers = new WeakMap();

function componentAbortController(component) {
  const AbortControllerConstructor = component?.ownerDocument?.defaultView?.AbortController || AbortController;
  return new AbortControllerConstructor();
}

function setupMenu(menu) {
  if (menuCleanups.has(menu)) return;
  const documentRoot = menu.ownerDocument;
  const controller = componentAbortController(menu);
  const summary = menu.querySelector('summary');

  menu.addEventListener('toggle', () => {
    summary?.setAttribute('aria-label', menu.open ? 'Close menu' : 'Open menu');
    if (menu.open) documentRoot.documentElement.dataset.menuOpen = 'true';
    else delete documentRoot.documentElement.dataset.menuOpen;
    if (menu.open) focusableElements(menu.querySelector('.runaway-menu__panel') ?? menu)[0]?.focus();
  }, { signal: controller.signal });

  menu.addEventListener('click', (event) => {
    if (!event.target.closest?.('a[href]')) return;
    menu.open = false;
    delete documentRoot.documentElement.dataset.menuOpen;
  }, { signal: controller.signal });

  menu.addEventListener('keydown', (event) => {
    if (!menu.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      menu.open = false;
      summary?.focus();
      return;
    }
    trapFocus(event, menu);
  }, { signal: controller.signal });

  const cleanup = () => {
    controller.abort();
    menu.open = false;
    delete documentRoot.documentElement.dataset.menuOpen;
    menuCleanups.delete(menu);
  };
  menuCleanups.set(menu, cleanup);
}

function setupCartDrawer(drawer) {
  if (cartDrawerCleanups.has(drawer)) return;
  const documentRoot = drawer.ownerDocument;
  const viewport = documentRoot.defaultView;
  const existingDrawer = activeDrawers.get(documentRoot);
  if (existingDrawer && existingDrawer !== drawer) cartDrawerCleanups.get(existingDrawer)?.();

  const controller = componentAbortController(drawer);
  let disposed = false;
  activeDrawers.set(documentRoot, drawer);

  function mutationFocusTarget(control, quantity) {
    const item = control?.closest?.('[data-cart-item]');
    const nextItem = Number(quantity) === 0 ? item?.nextElementSibling : null;
    return {
      key: item?.dataset.cartKey || '',
      nextKey: nextItem?.matches?.('[data-cart-item]') ? nextItem.dataset.cartKey || '' : '',
      role: control?.matches?.('[data-cart-remove]') ? 'remove' : 'quantity',
      removing: Number(quantity) === 0,
    };
  }

  function restoreMutationFocus(target) {
    const roleSelector = target.role === 'remove' ? '[data-cart-remove]' : '[data-cart-quantity]';
    const key = target.removing ? target.nextKey : target.key;
    const item = [...drawer.querySelectorAll('[data-cart-item]')]
      .find((candidate) => candidate.dataset.cartKey === key);
    const replacement = item?.querySelector(roleSelector);
    const fallback = drawer.querySelector('[data-cart-drawer-close]:not([aria-hidden="true"])') ||
      drawer.querySelector('[data-cart-fallback]');
    (replacement || fallback)?.focus();
  }

  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCartDrawer(drawer);
      return;
    }
    trapFocus(event, drawer);
  }, { signal: controller.signal });

  drawer.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-cart-drawer-close]')) closeCartDrawer(drawer);
  }, { signal: controller.signal });

  documentRoot.addEventListener('runaway:cart-open', (event) => {
    openCartDrawer(drawer, event.detail?.trigger);
  }, { signal: controller.signal });

  documentRoot.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-cart-trigger]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const menuSummary = trigger.closest('[data-menu]')?.querySelector(':scope > summary');
    openCartDrawer(drawer, menuSummary || trigger);
  }, { signal: controller.signal });

  function navigateToFreshCart() {
    const cartPage = documentRoot.querySelector('[data-cart-page]');
    if (!cartPage) return false;
    if (cartPage.dataset.cartRefreshing === 'true') return true;

    cartPage.dataset.cartRefreshing = 'true';
    cartPage.setAttribute('aria-busy', 'true');
    cartPage.inert = true;
    cartPage.querySelectorAll('button, input, select, textarea').forEach((control) => {
      control.disabled = true;
    });
    viewport.location.assign(cartRoute('', documentRoot));
    return true;
  }

  async function mutate(line, quantity, control, nativeFallbackUrl = '') {
    const liveRegion = drawer.querySelector('[data-cart-live]');
    const focusTarget = mutationFocusTarget(control, quantity);
    if (liveRegion) liveRegion.textContent = '';
    control?.setAttribute('aria-busy', 'true');
    try {
      const fetcher = viewport.fetch.bind(viewport);
      const cart = await changeItem(line, quantity, fetcher, documentRoot);
      if (disposed) return;
      if (navigateToFreshCart()) return;
      renderCartState(drawer, cart);
      dispatchCartChange(cart, documentRoot);
      restoreMutationFocus(focusTarget);
    } catch (error) {
      if (disposed) return;
      if (nativeFallbackUrl) {
        viewport.location.assign(nativeFallbackUrl);
        return;
      }
      if (control?.matches('[data-cart-quantity]')) {
        control.value = control.dataset.currentQuantity || control.value;
      }
      if (liveRegion) liveRegion.textContent = error.message;
    } finally {
      control?.removeAttribute('aria-busy');
    }
  }

  drawer.addEventListener('change', (event) => {
    const quantity = event.target.closest?.('[data-cart-quantity]');
    if (!quantity) return;
    const nextQuantity = Number(quantity.value);
    if (!Number.isInteger(nextQuantity) || nextQuantity < 0) {
      quantity.value = quantity.dataset.currentQuantity || '0';
      const liveRegion = drawer.querySelector('[data-cart-live]');
      if (liveRegion) liveRegion.textContent = 'Enter a whole quantity of zero or more.';
      return;
    }
    mutate(quantity.dataset.line, nextQuantity, quantity);
  }, { signal: controller.signal });

  drawer.addEventListener('click', (event) => {
    const remove = event.target.closest?.('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    mutate(remove.dataset.line, 0, remove, remove.getAttribute('href') || cartRoute('', documentRoot));
  }, { signal: controller.signal });

  const cleanup = () => {
    disposed = true;
    controller.abort();
    drawer.hidden = true;
    drawer.__runawayReturnFocus = null;
    delete documentRoot.documentElement.dataset.cartOpen;
    if (activeDrawers.get(documentRoot) === drawer) activeDrawers.delete(documentRoot);
    cartDrawerCleanups.delete(drawer);
  };
  cartDrawerCleanups.set(drawer, cleanup);
}

function setHidden(element, hidden) {
  if (!element) return;
  element.hidden = hidden;
}

function updateVariantUrl(variant, viewport = globalThis) {
  if (!viewport?.history?.replaceState || !viewport.location) return;
  const url = new URL(viewport.location.href);
  if (variant) url.searchParams.set('variant', String(variant.id));
  else url.searchParams.delete('variant');
  viewport.history.replaceState({}, '', url);
}

function setupProduct(productRoot) {
  if (productCleanups.has(productRoot)) return;
  const documentRoot = productRoot.ownerDocument;
  const viewport = documentRoot.defaultView;
  const productJson = productRoot.querySelector('[data-product-json]');
  const form = productRoot.querySelector('form');
  const variantInput = form?.querySelector('[data-variant-id]');
  const authoritativeVariant = form?.querySelector('[data-authoritative-variant]');
  const optionSelectorGroup = form?.querySelector('[data-option-selectors]');
  const purchaseButton = form?.querySelector('[data-add-to-cart]');
  const acceleratedCheckout = form?.querySelector('[data-accelerated-checkout]');
  const productHeading = productRoot.querySelector('h1');
  const error = form?.querySelector('[data-product-error]');
  const price = productRoot.querySelector('[data-product-price]');
  const comparePrice = productRoot.querySelector('[data-compare-price]');
  const availability = productRoot.querySelector('[data-product-availability]');
  const sticky = productRoot.querySelector('[data-sticky-purchase]');
  const stickyPrice = sticky?.querySelector('[data-sticky-price]');
  const stickyAvailability = sticky?.querySelector('[data-sticky-availability]');
  const stickyButton = sticky?.querySelector('[data-sticky-submit]');
  const optionSelectors = [...productRoot.querySelectorAll('[data-option-selector]')]
    .sort((left, right) => Number(left.dataset.optionPosition) - Number(right.dataset.optionPosition));

  if (!productJson || !form || !variantInput || !purchaseButton) return;

  let product;
  try {
    product = JSON.parse(productJson.textContent);
  } catch {
    if (error) error.textContent = 'Product options could not be loaded. Refresh this page to try again.';
    return;
  }

  const variantOptionsAreValid = Array.isArray(product?.variants)
    && product.variants.length > 0
    && product.variants.every((variant) => variant?.id != null && Array.isArray(variant.options));
  const hasProgressiveOptions = optionSelectors.length > 0;
  const usesAuthoritativeSelect = variantInput.matches('select');
  const selectedVariant = variantOptionsAreValid
    ? product.variants.find((variant) => String(variant.id) === variantInput.value)
    : null;
  const optionStructureIsValid = usesAuthoritativeSelect
    ? Boolean(authoritativeVariant && optionSelectorGroup && hasProgressiveOptions
      && selectedVariant && selectedVariant.options.length >= optionSelectors.length)
    : Boolean(!authoritativeVariant && !optionSelectorGroup && !hasProgressiveOptions && selectedVariant);
  if (!variantOptionsAreValid || !optionStructureIsValid) {
    if (error) error.textContent = 'Product options could not be loaded. Refresh this page to try again.';
    return;
  }

  const controller = componentAbortController(productRoot);
  let disposed = false;
  let cleanupStickyObservers = () => {};

  const IntersectionObserverConstructor = viewport?.IntersectionObserver;
  const inlinePurchaseTargets = [purchaseButton, acceleratedCheckout].filter(Boolean);
  if (sticky && IntersectionObserverConstructor && inlinePurchaseTargets.length > 0) {
    const visibleTargets = new Map();
    let headingOccludesSticky = false;
    let headingObserver;
    let resizeFrame;
    const targetIsVisible = (target) => {
      const box = target.getBoundingClientRect();
      return box.bottom > 0 && box.top < viewport.innerHeight && box.right > 0 && box.left < viewport.innerWidth;
    };
    const refreshPurchaseVisibility = () => {
      inlinePurchaseTargets.forEach((target) => visibleTargets.set(target, targetIsVisible(target)));
    };
    const updateStickyVisibility = () => {
      sticky.dataset.stickyVisible = String(!headingOccludesSticky && ![...visibleTargets.values()].some(Boolean));
    };
    refreshPurchaseVisibility();
    const purchaseObserver = new IntersectionObserverConstructor((entries) => {
      entries.forEach((entry) => visibleTargets.set(entry.target, entry.isIntersecting));
      updateStickyVisibility();
    }, { threshold: 0.05 });
    inlinePurchaseTargets.forEach((target) => purchaseObserver.observe(target));
    const rebuildHeadingObserver = () => {
      headingObserver?.disconnect();
      refreshPurchaseVisibility();
      if (!productHeading) {
        headingOccludesSticky = false;
        updateStickyVisibility();
        return;
      }
      const stickyOcclusionHeight = Number.parseFloat(viewport.getComputedStyle(sticky).minHeight) || 72;
      const stickyOcclusionTop = viewport.innerHeight - stickyOcclusionHeight;
      const headingBox = productHeading.getBoundingClientRect();
      headingOccludesSticky = headingBox.bottom > stickyOcclusionTop && headingBox.top < viewport.innerHeight;
      updateStickyVisibility();
      headingObserver = new IntersectionObserverConstructor((entries) => {
        headingOccludesSticky = entries.some((entry) => entry.isIntersecting);
        updateStickyVisibility();
      }, {
        rootMargin: `-${Math.max(0, stickyOcclusionTop)}px 0px 0px 0px`,
        threshold: 0.01,
      });
      headingObserver.observe(productHeading);
    };
    const scheduleHeadingObserverRebuild = () => {
      if (resizeFrame) viewport.cancelAnimationFrame(resizeFrame);
      resizeFrame = viewport.requestAnimationFrame(rebuildHeadingObserver);
    };
    const handlePageHide = (event) => {
      if (!event.persisted) cleanupStickyObservers();
    };
    cleanupStickyObservers = () => {
      purchaseObserver.disconnect();
      headingObserver?.disconnect();
      if (resizeFrame) viewport.cancelAnimationFrame(resizeFrame);
      viewport.removeEventListener('resize', scheduleHeadingObserverRebuild);
      viewport.removeEventListener('pagehide', handlePageHide);
    };
    rebuildHeadingObserver();
    viewport.addEventListener('resize', scheduleHeadingObserverRebuild, { passive: true, signal: controller.signal });
    viewport.addEventListener('pagehide', handlePageHide, { signal: controller.signal });
  }

  const availableLabel = purchaseButton.dataset.availableLabel || 'Add to cart';
  const soldOutLabel = purchaseButton.dataset.soldOutLabel || 'Sold out';

  function renderVariant(variant, { updateUrl = true } = {}) {
    const isAvailable = Boolean(variant?.available);
    const availabilityLabel = variant ? (isAvailable ? 'Available' : 'Sold out') : 'Unavailable';
    const buttonLabel = isAvailable ? availableLabel : soldOutLabel;

    if (variant) {
      variantInput.value = String(variant.id);
      variantInput.dispatchEvent(new viewport.Event('change', { bubbles: true }));
    }
    purchaseButton.disabled = !isAvailable;
    purchaseButton.textContent = buttonLabel;
    if (availability) {
      availability.textContent = availabilityLabel;
      availability.dataset.available = String(isAvailable);
    }
    setHidden(acceleratedCheckout, !isAvailable);

    if (stickyButton) {
      stickyButton.disabled = !isAvailable;
      stickyButton.textContent = buttonLabel;
    }
    if (stickyAvailability) {
      stickyAvailability.textContent = availabilityLabel;
      stickyAvailability.dataset.available = String(isAvailable);
    }

    if (variant) {
      const moneyFormat = productRoot.dataset.moneyFormat || '${{ amount }}';
      const renderedPrice = formatMoney(variant.price, moneyFormat);
      if (price) price.textContent = renderedPrice;
      if (stickyPrice) stickyPrice.textContent = renderedPrice;
      setHidden(price, false);
      setHidden(stickyPrice, false);
      const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
      if (comparePrice) {
        comparePrice.textContent = hasComparePrice
          ? formatMoney(variant.compare_at_price, moneyFormat)
          : '';
        setHidden(comparePrice, !hasComparePrice);
      }
      if (error) error.textContent = '';
    } else {
      if (price) price.textContent = '';
      if (comparePrice) comparePrice.textContent = '';
      if (stickyPrice) stickyPrice.textContent = '';
      setHidden(price, true);
      setHidden(comparePrice, true);
      setHidden(stickyPrice, true);
      if (error) error.textContent = 'This option combination is unavailable.';
    }

    if (updateUrl) updateVariantUrl(variant, viewport);
  }

  optionSelectors.forEach((selector) => {
    selector.addEventListener('change', () => {
      renderVariant(selectVariant(product, optionSelectors.map((option) => option.value)));
    }, { signal: controller.signal });
  });

  form.addEventListener('submit', async (event) => {
    if (!event.submitter?.matches?.('[data-add-to-cart]') || typeof viewport.fetch !== 'function') return;
    event.preventDefault();
    purchaseButton.disabled = true;
    if (error) error.textContent = '';

    const drawer = productRoot.ownerDocument.querySelector('[data-cart-drawer]');
    let itemWasAdded = false;
    try {
      const fetcher = viewport.fetch.bind(viewport);
      await addItem(new viewport.FormData(form), fetcher, documentRoot);
      if (disposed) return;
      itemWasAdded = true;
      const cart = await getCart(fetcher, documentRoot);
      if (disposed) return;
      if (drawer) {
        renderCartState(drawer, cart);
        openCartDrawer(drawer, event.submitter);
      }
      dispatchCartChange(cart, productRoot.ownerDocument);
    } catch (cartError) {
      if (disposed) return;
      if (!itemWasAdded) {
        const FormElement = productRoot.ownerDocument.defaultView.HTMLFormElement;
        FormElement.prototype.submit.call(form);
        return;
      }

      const cartUrl = drawer?.querySelector('[data-cart-fallback]')?.getAttribute('href') || cartRoute('', documentRoot);
      viewport.location.assign(cartUrl);
    } finally {
      if (!disposed) purchaseButton.disabled = false;
    }
  }, { signal: controller.signal });

  const cleanup = () => {
    disposed = true;
    controller.abort();
    cleanupStickyObservers();
    if (optionSelectorGroup) {
      optionSelectorGroup.hidden = true;
      optionSelectorGroup.setAttribute('inert', '');
      optionSelectors.forEach((selector) => { selector.disabled = true; });
    }
    if (authoritativeVariant) authoritativeVariant.hidden = false;
    productCleanups.delete(productRoot);
  };
  productCleanups.set(productRoot, cleanup);

  if (optionSelectorGroup) {
    optionSelectors.forEach((selector, index) => {
      selector.value = selectedVariant.options[index];
    });
    renderVariant(selectedVariant, { updateUrl: false });
    optionSelectors.forEach((selector) => { selector.disabled = false; });
    optionSelectorGroup.hidden = false;
    optionSelectorGroup.removeAttribute('inert');
    authoritativeVariant.hidden = true;
  }
}

function componentsWithin(root, selector) {
  if (!root) return [];
  const components = root.matches?.(selector) ? [root] : [];
  if (typeof root.querySelectorAll === 'function') components.push(...root.querySelectorAll(selector));
  return components;
}

function initializeComponents(root) {
  componentsWithin(root, '[data-menu]').forEach(setupMenu);
  componentsWithin(root, '[data-product-root]').forEach(setupProduct);
  componentsWithin(root, '[data-cart-drawer]').forEach(setupCartDrawer);
}

export function disposeStorefront(root) {
  componentsWithin(root, '[data-product-root]').forEach((component) => productCleanups.get(component)?.());
  componentsWithin(root, '[data-menu]').forEach((component) => menuCleanups.get(component)?.());
  componentsWithin(root, '[data-cart-drawer]').forEach((component) => cartDrawerCleanups.get(component)?.());

  if (root?.nodeType === 9) {
    documentCleanups.get(root)?.();
  }
}

function setupDocumentLifecycle(documentRoot) {
  if (!documentRoot || documentCleanups.has(documentRoot)) return;
  const controller = componentAbortController(documentRoot.documentElement);

  documentRoot.addEventListener(CART_CHANGE_EVENT, (event) => {
    updateCartCount(event, documentRoot);
  }, { signal: controller.signal });
  documentRoot.addEventListener('shopify:section:load', (event) => {
    initializeComponents(event.target);
  }, { signal: controller.signal });
  documentRoot.addEventListener('shopify:section:unload', (event) => {
    disposeStorefront(event.target);
  }, { signal: controller.signal });

  const cleanup = () => {
    controller.abort();
    documentCleanups.delete(documentRoot);
  };
  documentCleanups.set(documentRoot, cleanup);
}

export function initializeStorefront(root = document) {
  const documentRoot = root?.nodeType === 9 ? root : root?.ownerDocument;
  setupDocumentLifecycle(documentRoot);
  initializeComponents(root);
}

if (typeof document !== 'undefined') {
  initializeStorefront(document);
}
